// src/pages/profile-settings/accountEmailChangeService.js
import supabase, { getRedirectUrl } from "@/services/supabaseClient";

const PENDING_EMAIL_CHANGE_KEY = "afrimed:account_email_change:pending_v1";

export function normalizeAccountEmail(value = "") {
  return String(value || "").trim().toLowerCase();
}

export function isValidAccountEmail(value = "") {
  return /^\S+@\S+\.\S+$/.test(normalizeAccountEmail(value));
}

export function readPendingAccountEmailChange() {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage?.getItem(PENDING_EMAIL_CHANGE_KEY) || "null");
  } catch {
    return null;
  }
}

export function writePendingAccountEmailChange(payload) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(PENDING_EMAIL_CHANGE_KEY, JSON.stringify(payload || {}));
  } catch {
    // Local persistence is convenience only; the database request is the source of truth.
  }
}

export function clearPendingAccountEmailChange() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.removeItem(PENDING_EMAIL_CHANGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export async function getCurrentAuthEmail() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return {
    user: data?.user || null,
    email: normalizeAccountEmail(data?.user?.email || ""),
    emailConfirmedAt:
      data?.user?.email_confirmed_at || data?.user?.emailConfirmedAt || null,
  };
}

async function recordAuthEmailChangeResult(requestId, status, errorMessage = "") {
  if (!requestId) return;
  try {
    await supabase.rpc("record_account_email_change_auth_result", {
      p_request_id: requestId,
      p_status: status,
      p_error: errorMessage || null,
    });
  } catch {
    // Best effort. The initial request row is already durable.
  }
}

export async function requestAccountEmailChange({ lane, newEmail }) {
  const cleanLane = String(lane || "").trim().toLowerCase();
  const cleanEmail = normalizeAccountEmail(newEmail);
  if (!["doctor", "patient", "admin", "pharmacy", "institution", "practice_team"].includes(cleanLane)) {
    const err = new Error("Invalid account lane.");
    err.code = "invalid_lane";
    throw err;
  }
  if (!isValidAccountEmail(cleanEmail)) {
    const err = new Error("Enter a valid email address.");
    err.code = "invalid_email";
    throw err;
  }

  const { data: requestData, error: requestError } = await supabase.rpc(
    "request_account_email_change",
    {
      p_new_email: cleanEmail,
      p_lane: cleanLane,
    },
  );
  if (requestError) {
    const err = new Error(requestError.message || "Email change request failed.");
    err.code = requestError.message || requestError.code || "request_failed";
    throw err;
  }

  const request = Array.isArray(requestData) ? requestData[0] : requestData;
  const requestId = request?.request_id || request?.id;
  writePendingAccountEmailChange({
    request_id: requestId,
    lane: cleanLane,
    old_email: request?.old_email || "",
    new_email: cleanEmail,
    expires_at: request?.expires_at || "",
    requested_at: new Date().toISOString(),
  });

  const callbackUrl = getRedirectUrl("/auth/callback?account_email_change=1");
  const { error: updateError } = await supabase.auth.updateUser(
    { email: cleanEmail },
    { emailRedirectTo: callbackUrl },
  );

  if (updateError) {
    await recordAuthEmailChangeResult(requestId, "failed", updateError.message);
    const err = new Error(updateError.message || "Could not send confirmation link.");
    err.code = updateError.message || updateError.code || "auth_update_failed";
    throw err;
  }

  await recordAuthEmailChangeResult(requestId, "auth_update_sent");
  return {
    ok: true,
    request_id: requestId,
    old_email: request?.old_email || "",
    new_email: cleanEmail,
    expires_at: request?.expires_at || "",
  };
}

export async function finalizePendingAccountEmailChange({ lane } = {}) {
  const pending = readPendingAccountEmailChange();
  const cleanLane = String(lane || pending?.lane || "").trim().toLowerCase();

  const { data, error } = await supabase.rpc("finalize_account_email_change", {
    p_lane: cleanLane || null,
  });
  if (error) throw error;

  const result = Array.isArray(data) ? data[0] : data;
  if (result?.ok) clearPendingAccountEmailChange();
  return result || { ok: false };
}
